import { adviseOnInbreeding, describeRelatedness } from "@breenova/domain";
import {
  Alert,
  BottomSheet,
  Button,
  Field,
  FieldHint,
  Label,
  Picker,
  StatusPill,
  Textarea,
  cn,
} from "@breenova/ui";
import { Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";

import { DateField } from "@/components/date-field";
import { useBirds, useLocations, usePedigreeCoefficient, type BirdSummary } from "@/lib/birds";
import {
  useCreatePair,
  useDeletePair,
  useUpdatePair,
  type PairSummary,
} from "@/lib/breeding";

export type PairSheetState =
  | { mode: "create"; seasonId: string | undefined; seasonLabel: string | undefined }
  | { mode: "edit"; pair: PairSummary };

const BAND_TONES = {
  unrelated: "positive",
  acceptable: "positive",
  caution: "attention",
  avoid: "critical",
} as const;

/**
 * Put a pair together, or edit one that already exists.
 *
 * The two birds are chosen only on the way in. `PairsUpdateCommand` carries no sire or
 * dam, and rightly: swapping a bird under a pair that already has rounds would silently
 * re-parent every chick recorded from it. Getting the pairing wrong is fixed by removing
 * the pair, which the server allows exactly while no round exists.
 */
export function PairSheet({
  state,
  onClose,
  onError,
}: {
  state: PairSheetState;
  onClose: () => void;
  onError: (message: string | null) => void;
}) {
  const editing = state.mode === "edit" ? state.pair : undefined;

  // Which season the new pair lands in. Worth saying out loud: the screen behind this
  // sheet can be showing any season, and the pair follows the screen rather than whichever
  // one happens to be current.
  const seasonId = state.mode === "create" ? state.seasonId : undefined;
  const seasonName = state.mode === "create" ? state.seasonLabel : undefined;

  const create = useCreatePair();
  const update = useUpdatePair();
  const remove = useDeletePair();

  // Only birds that are actually in the aviary. A sold or dead bird is still a legal
  // pairing as far as the server is concerned — it checks sex, not whereabouts — but
  // offering one is offering a round that cannot happen.
  const cocks = useBirds({ sex: "Male", status: "InAviary", pageSize: 200, sort: "RingNumber" });
  const hens = useBirds({ sex: "Female", status: "InAviary", pageSize: 200, sort: "RingNumber" });
  const cages = useLocations(true);

  const [sireId, setSireId] = useState("");
  const [damId, setDamId] = useState("");
  const [locationId, setLocationId] = useState(editing?.locationId ?? "");
  const [pairedOn, setPairedOn] = useState(editing?.pairedOn ?? "");
  const [separatedOn, setSeparatedOn] = useState(editing?.separatedOn ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Both birds chosen: the pedigree already knows what they share, and this is the last
  // moment the answer is still free to act on.
  const score = usePedigreeCoefficient(sireId || undefined, damId || undefined);

  const busy = create.isPending || update.isPending || remove.isPending;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setMessage(null);

    if (editing) {
      if (separatedOn && pairedOn && separatedOn < pairedOn) {
        setMessage("A pair cannot be separated before it was put together.");
        return;
      }
    } else if (!sireId || !damId) {
      setMessage("A pair is a cock and a hen. Choose both.");
      return;
    }

    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          body: {
            id: editing.id,
            // The handler assigns these straight across, so what is sent is what the
            // pair ends up with — an empty field clears rather than preserves.
            locationId: locationId || null,
            pairedOn: pairedOn || null,
            separatedOn: separatedOn || null,
            notes: notes.trim() || null,
          },
        });
      } else {
        await create.mutateAsync({
          sireId,
          damId,
          // The season the screen is showing, not whichever is current: a breeder looking
          // at last year and adding a pair means last year.
          breedingSeasonId: seasonId ?? null,
          locationId: locationId || null,
          // Null is today on the server, which is what putting a pair together usually is.
          pairedOn: pairedOn || null,
          notes: notes.trim() || null,
        });
      }

      onError(null);
      onClose();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "That could not be saved.");
    }
  }

  async function confirmRemove(): Promise<void> {
    if (!editing) {
      return;
    }

    try {
      await remove.mutateAsync(editing.id);
      onError(null);
      onClose();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "That could not be removed.");
      setConfirmingRemove(false);
    }
  }

  return (
    <BottomSheet
      open
      onClose={onClose}
      title={editing ? "This pair" : "Put a pair together"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>

          <Button type="submit" form="pair-form" className="flex-1" disabled={busy}>
            {busy ? "Saving…" : editing ? "Save" : "Pair them"}
          </Button>
        </>
      }
    >
      <form id="pair-form" onSubmit={(event) => void submit(event)} className="flex flex-col gap-lg">
        {message ? <Alert tone="error">{message}</Alert> : null}

        {editing ? (
          <div className="flex flex-col gap-hair rounded-inset bg-surface-container-low px-lg py-md">
            <span className="text-label text-on-surface" data-numeric>
              {editing.sireRingNumber ?? "Unringed"} ♂ × {editing.damRingNumber ?? "Unringed"} ♀
            </span>
            <span className="text-caption text-on-surface-variant" data-numeric>
              {editing.seasonName
                ? `${editing.season} · ${editing.seasonName}`
                : editing.season}{" "}
              · {editing.roundCount}{" "}
              {editing.roundCount === 1 ? "round" : "rounds"}
              {editing.isHybridPair ? " · mule pair" : ""}
            </span>
          </div>
        ) : (
          <>
            {seasonName ? (
              <p className="text-caption text-on-surface-variant" data-numeric>
                Going into the {seasonName} season.
              </p>
            ) : null}

            <Field>
              <Label>Cock</Label>
              <Picker
                label="Cock"
                placeholder="Choose the sire…"
                emptyLabel={
                  cocks.isPending ? "Loading…" : "No cock in the aviary is recorded as male"
                }
                value={sireId}
                onChange={setSireId}
                options={(cocks.data?.items ?? []).map(birdOption)}
              />
              <FieldHint>
                Only birds recorded as male and in the aviary. A bird whose sex is unknown
                cannot be a sire — record its sex first.
              </FieldHint>
            </Field>

            <Field>
              <Label>Hen</Label>
              <Picker
                label="Hen"
                placeholder="Choose the dam…"
                emptyLabel={
                  hens.isPending ? "Loading…" : "No hen in the aviary is recorded as female"
                }
                value={damId}
                onChange={setDamId}
                options={(hens.data?.items ?? []).map(birdOption)}
              />
            </Field>

            {/* A cross-species pairing is not a mistake — it is the point of a good part of
                this hobby — so it is announced rather than warned about. */}
            {sireId && damId && isHybrid(cocks.data?.items, hens.data?.items, sireId, damId) ? (
              <Alert tone="info">
                Different species, so this is a mule pair. Every chick out of it is recorded
                as a hybrid without you having to remember.
              </Alert>
            ) : null}

            {score.data ? (
              <Relatedness
                coefficient={score.data.coefficient}
                completeness={score.data.pedigreeCompleteness}
              />
            ) : null}
          </>
        )}

        <Field>
          <Label>Cage</Label>
          <Picker
            label="Cage"
            placeholder="Not placed"
            emptyLabel="No cages recorded yet"
            value={locationId}
            onChange={setLocationId}
            options={[
              { value: "", label: "Not placed" },
              ...(cages.data?.locations ?? []).map((cage) => ({
                value: cage.id,
                label: cage.path,
                ...(cage.birdCount > 0 ? { hint: `${cage.birdCount} birds` } : {}),
              })),
            ]}
          />
        </Field>

        <Field>
          <Label>Paired on</Label>
          <DateField
            label="Paired on"
            value={pairedOn}
            onChange={setPairedOn}
            placeholder={editing ? "Not recorded" : "Today"}
          />
        </Field>

        {/*
         * Separating is the honest end of a pair: the rounds it produced stay, and the
         * pair stops being one of the ones running. Only offered on an existing pair,
         * because a pair created already separated is a record of something that did not
         * happen this way round.
         */}
        {editing ? (
          <Field>
            <Label>Separated on</Label>
            <DateField
              label="Separated on"
              value={separatedOn}
              onChange={setSeparatedOn}
              placeholder="Still together"
            />
            <FieldHint>
              Set this when the birds come apart. Every round already recorded stays with
              the pair.
            </FieldHint>
          </Field>
        ) : null}

        <Field>
          <Label htmlFor="pair-notes">Notes</Label>
          <Textarea
            id="pair-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={2}
            maxLength={2000}
          />
        </Field>

        {/*
         * Removal is offered only while the pair has no rounds. The server refuses it
         * after that — the nests carry the parentage of every chick — and a button whose
         * only outcome is that refusal is worse than the separation date above, which is
         * what a breeder actually wants in that case.
         */}
        {editing && editing.roundCount === 0 ? (
          confirmingRemove ? (
            <div className="flex flex-col gap-sm rounded-inset bg-error-container p-lg">
              <p className="text-label text-on-error-container">Remove this pair?</p>
              <p className="text-caption text-on-error-container">
                Nothing has been recorded from it yet, so nothing is lost. The two birds
                are untouched.
              </p>

              <div className="flex gap-sm">
                <Button
                  type="button"
                  variant="danger"
                  disabled={busy}
                  onClick={() => void confirmRemove()}
                >
                  Remove
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => setConfirmingRemove(false)}
                >
                  Keep it
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              className="self-start text-error"
              disabled={busy}
              onClick={() => setConfirmingRemove(true)}
            >
              <Trash2 className="size-4" aria-hidden />
              Remove this pair
            </Button>
          )
        ) : null}
      </form>
    </BottomSheet>
  );
}

/**
 * What the two birds already share, before the pairing exists.
 *
 * The same numbers the pedigree tab prints, at the moment they can still change the
 * answer. An unknown ancestor counts as unrelated, so a sparse pedigree always understates
 * the figure — which is why the completeness is said out loud beside it rather than left
 * for the breeder to remember.
 */
function Relatedness({
  coefficient,
  completeness,
}: {
  coefficient: number;
  completeness: number;
}) {
  const advice = adviseOnInbreeding(coefficient);

  return (
    <div
      className={cn(
        "flex flex-col gap-sm rounded-inset bg-surface-container-low px-lg py-md",
      )}
    >
      <div className="flex items-center justify-between gap-md">
        <span className="text-label-caps uppercase text-on-surface-variant">Relatedness</span>
        <StatusPill tone={BAND_TONES[advice.band]}>{advice.label}</StatusPill>
      </div>

      <p className="text-caption text-on-surface-variant">
        {describeRelatedness(coefficient)}
        {completeness < 0.5
          ? ` Only ${Math.round(completeness * 100)}% of the pedigree behind these two is recorded, so this reads low.`
          : ""}
      </p>
    </div>
  );
}

/** A bird as a picker row: the ring number leads, because that is its name. */
function birdOption(bird: BirdSummary) {
  const hint = [bird.speciesName, bird.locationCode].filter(Boolean).join(" · ");

  return {
    value: bird.id,
    label: bird.ringNumber ?? "Unringed",
    ...(hint ? { hint } : {}),
  };
}

/** Whether the two chosen birds are of different species, which makes this a mule pair. */
function isHybrid(
  cocks: readonly BirdSummary[] | undefined,
  hens: readonly BirdSummary[] | undefined,
  sireId: string,
  damId: string,
): boolean {
  const sire = cocks?.find((bird) => bird.id === sireId);
  const dam = hens?.find((bird) => bird.id === damId);

  return Boolean(sire && dam && sire.speciesId !== dam.speciesId);
}
